import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { EmptyState } from "@/components/EmptyState";
import { Skel } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { relativeDate } from "@/lib/format";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function Equipo() {
    const [team, setTeam] = useState(null);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/team");
            setTeam(data);
        } catch (e) { toast.error(errMsg(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const copy = () => {
        navigator.clipboard?.writeText(team.invite_link);
        setCopied(true);
        toast.success("Enlace copiado");
        setTimeout(() => setCopied(false), 2000);
    };

    if (!team) {
        return (
            <div className="space-y-6">
                <Skel className="h-9 w-40" />
                <Skel className="h-40 w-full rounded-2xl" />
                <Skel className="h-64 w-full rounded-2xl" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="equipo-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-nm-ink">Equipo</h1>
                <p className="mt-1 text-nm-muted">Invita a nuevas personas. Al unirse con tu enlace, arrancan su propia ruta.</p>
            </div>

            {/* Enlace de invitación */}
            <section className="relative overflow-hidden rounded-2xl border border-nm-line bg-nm-panel p-6 text-white">
                <div className="absolute inset-0 grain opacity-50"
                    style={{ background: "radial-gradient(90% 120% at 10% 0%, rgba(139,92,246,0.7) 0%, #0b0912 60%)" }} />
                <div className="relative">
                    <p className="text-xs font-medium uppercase tracking-wider text-white/60">Tu enlace de invitación</p>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                        <Input readOnly value={team.invite_link} data-testid="invite-link-input"
                            className="border-white/20 bg-nm-surface/10 text-white placeholder:text-white/50" />
                        <Button onClick={copy} data-testid="copy-invite-button"
                            className="shrink-0 btn-grad text-white hover:opacity-90">
                            {copied ? <><Check className="mr-1 h-4 w-4" /> Copiado</> : <><Copy className="mr-1 h-4 w-4" /> Copiar enlace</>}
                        </Button>
                    </div>
                    <p className="mt-3 text-sm text-white/70">Código: <span className="font-mono font-semibold text-white">{team.invite_code}</span></p>
                </div>
            </section>

            {team.sponsor && (
                <div className="rounded-2xl border border-nm-line bg-nm-surface p-4 text-sm text-nm-muted">
                    Te incorporó <span className="font-semibold text-nm-ink">{team.sponsor.name}</span>.
                </div>
            )}

            {/* Miembros */}
            <section>
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-nm-ink">Tu equipo</h2>
                    <span className="text-sm text-nm-muted tabular-nums">{team.total} miembros</span>
                </div>
                {team.miembros.length === 0 ? (
                    <EmptyState testId="equipo-empty" title="Aún no tienes equipo"
                        description="Comparte tu enlace con quien busque emprender o ingresos extra. Cuando se unan, aparecerán aquí." />
                ) : (
                    <div className="divide-y divide-nm-line overflow-hidden rounded-2xl border border-nm-line bg-nm-surface">
                        {team.miembros.map((m) => (
                            <div key={m.email} className="flex items-center gap-4 p-4" data-testid={`team-member-${m.email}`}>
                                <InitialsAvatar name={m.name} />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate font-semibold text-nm-ink">{m.name}</p>
                                    <p className="truncate text-xs text-nm-muted">Se unió {relativeDate(m.joined_at)}</p>
                                </div>
                                <div className="w-28 shrink-0">
                                    <div className="flex items-center justify-between text-xs text-nm-muted">
                                        <span>Ruta</span>
                                        <span className="tabular-nums">{m.progreso}%</span>
                                    </div>
                                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-nm-line">
                                        <div className="h-full rounded-full bg-nm-accent" style={{ width: `${m.progreso}%` }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
